/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Box,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";

import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import SearchInput from "components/SearchInput";

import { useSnackbar } from "hooks/useSnackbar";

import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";

import { CMSUserRoleLabels } from "utils/tableHelper/userManagementTableHelper";
import { listUsers } from "services/userManagementService";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  inactiveChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  resetChip: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  notResetChip: {
    backgroundColor: "#2196f3",
    color: "white",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
}));

const UserManagement = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  // States
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);

  // Permissions - Only SUPER_ADMIN_DELETE can edit users (not SUPER_ADMIN)
  const hasCreatePermission = sessionHelper.hasRole(
    PermittedRoles.USER_MANAGEMENT?.CREATE_ROLES
  );
  const hasEditPermission = sessionHelper.hasRole([
    PermittedRoles.USER_MANAGEMENT?.DELETE_ROLES[0],
  ]);

  // Fetch users from API
  const fetchUsers = async (searchVal = "", pageNum = 0, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await listUsers({
        pageNo: pageNum + 1,
        pageSize: pageSize,
        search: searchVal,
        sortBy: "createdAt",
        sortDirection: -1,
      });

      if (response?.status && response?.data?.users) {
        const currentPageUsers = response.data.users;
        setUsers(currentPageUsers);

        // Calculate total count:
        // If we got fewer items than requested, we're on the last page
        // Total = (pageNum * pageSize) + current page items
        const calculatedTotal = pageNum * pageSize + currentPageUsers.length;

        // If this page has less than pageSize items, this is the last page
        // So the total is exactly what we calculated
        // Otherwise, we know there's at least more than this
        if (currentPageUsers.length < pageSize) {
          // Last page - exact total
          setTotalUsers(calculatedTotal);
        } else {
          // More data exists - set minimum total
          setTotalUsers(calculatedTotal + 1);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      showSnackbar(error?.message || "Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers(search, page, rowsPerPage);
  }, []);

  // Handle search
  const handleSearchChange = (value: any) => {
    setSearch(value);
    setPage(0);
    fetchUsers(value, 0, rowsPerPage);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchUsers(search, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchUsers(search, 0, newRowsPerPage);
  };

  // Edit user
  const handleEditUser = (userId: any) => {
    navigate(configData.routes.userManagement.edit?.replace(":id", userId));
  };

  // Create user
  const handleCreateUser = () => {
    navigate(configData.routes.userManagement.create);
  };

  return (
    <ContentShell
      title="User Management"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={search}
            placeholder="Search by username or email"
            onSearch={handleSearchChange}
          />
          {hasCreatePermission && (
            <CustomButton onClick={handleCreateUser}>Create User</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={classes.container}>
        {/* Users Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell className={classes.tableHeader}>Username</TableCell>
                <TableCell className={classes.tableHeader}>Email</TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Role
                </TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Status
                </TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  User Password Reset
                </TableCell>
                {hasEditPermission && (
                  <TableCell className={classes.tableHeader} align="center">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={hasEditPermission ? 6 : 5} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasEditPermission ? 6 : 5} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell align="center">
                      {CMSUserRoleLabels[user.role] || user.role}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.isActive ? "enabled" : "disabled"}
                        className={
                          user.isActive
                            ? classes.activeChip
                            : classes.inactiveChip
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          user.requiresPasswordReset
                            ? "Required"
                            : "Not Required"
                        }
                        className={
                          user.requiresPasswordReset
                            ? classes.resetChip
                            : classes.notResetChip
                        }
                        size="small"
                      />
                    </TableCell>
                    {hasEditPermission && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user._id)}
                          aria-label="edit user"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </ContentShell>
  );
};

export default UserManagement;
